import { supabase } from "../../lib/supabaseClient.ts";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import pawPrint from "../../assets/img/w_pawprint.png";

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingPromo, setUpdatingPromo] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [promoPercentage, setPromoPercentage] = useState("");
  const [showVariationsModal, setShowVariationsModal] = useState(false);
  const [editingVariation, setEditingVariation] = useState(null);
  const [variationForm, setVariationForm] = useState({
    name: "",
    price: "",
    weight: "",
    old_price: "",
    composition: "",
    analysis: "",
    ingredients: "",
    antioxidants: "",
    energy: "",
    image: ""

  });
  const [updatingVariationPromo, setUpdatingVariationPromo] = useState(false);
  const [showPromoPopup, setShowPromoPopup] = useState(false);
  const [currentVariation, setCurrentVariation] = useState(null);
  const [currentPromoType, setCurrentPromoType] = useState('percentage'); // 'percentage' or 'exact'
  const [promoInputValue, setPromoInputValue] = useState("");
  const [productPromoType, setProductPromoType] = useState(product?.promo_type || 'percentage');
  const [exactPromoPrice, setExactPromoPrice] = useState(product?.price_range || "");
  const [variations, setVariations] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (product) {
      setProductPromoType(product.promo_type || 'percentage');
      setExactPromoPrice(product.price_range || "");
      setPromoPercentage(product.promo_percentage || 10);
    }
  }, [product]);

  const openPromoPopup = (variation, type) => {
    setCurrentVariation(variation);
    setCurrentPromoType(type);
    setPromoInputValue(
      type === 'percentage'
        ? variation.promo_percentage || "10"
        : variation.price
    );
    setShowPromoPopup(true);
  };

  const submitPromoPopup = () => {
    if (!currentVariation) return;
    handleVariationPromoChange(currentVariation, currentPromoType, promoInputValue);
    setShowPromoPopup(false);
  };
  const handleProductPromoTypeChange = async (newType) => {
    try {
      let updateData = {
        promo_type: newType
      };

      // Get base price (use old_price if exists, otherwise current price_range)
      const basePrice = product.old_price
        ? parseFloat(product.old_price)
        : parseFloat(product.price_range);

      if (newType === 'percentage') {
        // Switch to percentage mode
        const percentage = product.promo_percentage || 10;
        const newPrice = basePrice * (1 - percentage / 100);

        updateData.price_range = newPrice;
        updateData.promo_percentage = percentage;

        // Store old_price if not already stored
        if (!product.old_price) {
          updateData.old_price = product.price_range;
        }

      } else if (newType === 'exact') {
        // Switch to exact price mode
        updateData.promo_percentage = null;

        // Store old_price if not already stored
        if (!product.old_price) {
          updateData.old_price = product.price_range;
        }
        // Keep current price_range as the exact promo price
      }

      const { data, error } = await supabase
        .from('products')
        .update(updateData)
        .eq('id', product.id)
        .select()
        .single();

      if (error) throw error;

      setProduct(data);
      setProductPromoType(newType);
      alert(`Type de promo changé en: ${newType === 'percentage' ? 'Pourcentage' : 'Prix exact'}`);

    } catch (error) {
      console.error('Error changing promo type:', error);
      alert('Erreur lors du changement de type de promo');
    }
  };

  const handleProductExactPriceChange = async () => {
    const newPrice = parseFloat(exactPromoPrice);

    if (isNaN(newPrice) || newPrice <= 0) {
      alert("Veuillez entrer un prix valide");
      return;
    }

    try {
      const updateData = {
        price_range: newPrice,
        promo_type: 'exact',
        promo_percentage: null
      };

      if (!product.old_price) {
        updateData.old_price = product.price_range;
      }

      const { data, error } = await supabase
        .from('products')
        .update(updateData)
        .eq('id', product.id)
        .select()
        .single();

      if (error) throw error;

      setProduct(data);
      alert('Prix promo mis à jour avec succès');
    } catch (error) {
      console.error('Error updating exact price:', error);
      alert('Erreur lors de la mise à jour du prix');
    }
  };
  // Formats the price for display with space as thousands separator
  const formatPrice = (price) => {
    const num = parseFloat(price.toString().replace(/\s/g, '').replace(/[^\d.-]/g, ''));
    if (isNaN(num)) return price;
    return num.toLocaleString('fr-FR');  // this will format like 2 430, 2 700, etc.
  };

  // Parses price (removes spaces and non-numeric characters for DB storage)
  const parsePrice = (price) => {
    return parseFloat(price.toString().replace(/\s/g, '').replace(/[^\d.-]/g, ''));
  };


  const handleToggleVariationPromo = async (variation) => {
    setUpdatingVariationPromo(true);

    try {
      const updatedPromo = !variation.promo;

      if (updatedPromo) {
        // ========== ACTIVATING PROMO ==========
        // Use default percentage-based promo on activation
        const basePrice = variation.old_price
          ? parseFloat(variation.old_price)
          : parseFloat(variation.price);

        const defaultPercentage = 10;
        const newPrice = basePrice * (1 - defaultPercentage / 100);

        const updatePayload = {
          promo: true,
          promo_type: 'percentage', // Default to percentage
          promo_percentage: defaultPercentage,
          price: newPrice,
          old_price: basePrice
        };

        const { error } = await supabase
          .from("product_variations")
          .update(updatePayload)
          .eq("id", variation.id);

        if (error) throw error;

        await refreshVariations(product.id);
        alert("Promo activée avec 10% de réduction par défaut. Vous pouvez modifier le type ou le montant.");

      } else {
        // ========== DEACTIVATING PROMO ==========
        // Restore original price
        const originalPrice = variation.old_price
          ? parseFloat(variation.old_price)
          : parseFloat(variation.price);

        const updatePayload = {
          promo: false,
          promo_type: null,
          promo_percentage: null,
          price: originalPrice,
          old_price: null
        };

        const { error } = await supabase
          .from("product_variations")
          .update(updatePayload)
          .eq("id", variation.id);

        if (error) throw error;

        await refreshVariations(product.id);
        alert("Promo retirée avec succès");
      }

    } catch (error) {
      console.error("Error toggling promo:", error.message);
      alert("Erreur lors de la mise à jour de la promo: " + error.message);
    } finally {
      setUpdatingVariationPromo(false);
    }
  };


  const handleVariationPromoChange = async (variation, promoType, value) => {
    try {
      let updateData = {
        promo: true,
        promo_type: promoType
      };

      if (promoType === 'percentage') {
        // ========== PERCENTAGE-BASED PROMO ==========
        const percentage = parseFloat(value);
        if (isNaN(percentage) || percentage < 0 || percentage > 100) {
          alert("Veuillez entrer un pourcentage valide entre 0 et 100");
          return;
        }

        // Get base price (original price before any discount)
        const basePrice = variation.old_price
          ? parsePrice(variation.old_price)
          : parsePrice(variation.price);

        // Calculate new discounted price
        const newPrice = basePrice * (1 - percentage / 100);

        updateData.promo_percentage = percentage;
        updateData.price = parsePrice(newPrice);

        // Store original price if not already stored
        if (!variation.old_price) {
          updateData.old_price = basePrice;
        }

      } else if (promoType === 'exact') {
        // ========== EXACT PRICE PROMO ==========
        const exactPrice = parseFloat(value);
        if (isNaN(exactPrice) || exactPrice <= 0) {
          alert("Veuillez entrer un prix valide");
          return;
        }

        // Store old price if not already stored
        if (!variation.old_price) {
          updateData.old_price = parsePrice(variation.price);
        }

        // Set the exact new price
        updateData.price = parsePrice(exactPrice);
        updateData.promo_percentage = null; // Clear percentage for exact price promo
      }

      // Update in database
      const { error } = await supabase
        .from('product_variations')
        .update(updateData)
        .eq('id', variation.id);

      if (error) throw error;

      // Refresh variations list
      await refreshVariations(product.id);

      const typeLabel = promoType === 'percentage' ? 'pourcentage' : 'prix exact';
      alert(`Promo ${typeLabel} mise à jour avec succès!`);

    } catch (error) {
      console.error('Error updating promo:', error);
      alert("Erreur lors de la mise à jour de la promo: " + error.message);
    }
  };

  // Replace your existing handleVariationPromoPercentageChange function:
  const handleVariationPromoPercentageChange = async (variation, percentage) => {
    const numPercentage = parseFloat(percentage);

    if (isNaN(numPercentage) || numPercentage < 0 || numPercentage > 100) {
      alert("Veuillez entrer un pourcentage valide entre 0 et 100.");
      return;
    }

    try {
      // Get base price (parse without spaces)
      const basePrice = variation.old_price
        ? parsePrice(variation.old_price)
        : parsePrice(variation.price);

      // Calculate new price with new percentage
      const calculatedPrice = variation.promo
        ? basePrice * (1 - numPercentage / 100)
        : basePrice;

      // Format with spaces
      const formattedPrice = calculatedPrice

      const { data, error } = await supabase
        .from("product_variations")
        .update({
          promo_percentage: numPercentage,
          price: parsePrice(calculatedPrice), // numeric only
        })
        .eq("id", variation.id)
        .select()
        .single();

      if (error) throw error;

      await refreshVariations(product.id);
      alert("Pourcentage promo mis à jour avec succès");
    } catch (error) {
      console.error("Error updating promo percentage:", error.message);
      alert("Erreur lors de la mise à jour: " + error.message);
    }
  };

  // Also update handleAddOrUpdateVariation to format prices:
  const handleAddOrUpdateVariation = async () => {
    const {
      name, price, weight, old_price, composition,
      analysis, ingredients, antioxidants, energy, image
    } = variationForm;

    if (!name || !price || !weight) return alert("Champs obligatoires manquants.");

    // Format prices with spaces before saving
    const numericPrice = parsePrice(price);
    const numericOldPrice = old_price ? parsePrice(old_price) : null;

    const payload = {
      product_id: product.id,
      variation_name: name,
      price: numericPrice,
      weight,
      old_price: numericOldPrice,
      composition: composition || null,
      analysis: analysis || null,
      ingredients: ingredients || null,
      antioxidants: antioxidants || null,
      energy: energy || null,
      image: image || null,
    };

    try {
      const { error } = editingVariation
        ? await supabase.from("product_variations").update(payload).eq("id", editingVariation.id)
        : await supabase.from("product_variations").insert(payload);

      if (error) throw error;

      refreshVariations(product.id);

      // Reset form
      setVariationForm({
        name: "", price: "", weight: "", old_price: "",
        composition: "", analysis: "", ingredients: "",
        antioxidants: "", energy: "", image: ""
      });

      setEditingVariation(null);
      setShowFormModal(false);
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la sauvegarde: " + err.message);
    }
  };

  const handleToggleAvailability = async (variation) => {
    try {
      const { error } = await supabase
        .from("product_variations")
        .update({ available: !variation.available })
        .eq("id", variation.id);

      if (error) throw error;

      refreshVariations(product.id); // recharge la liste
    } catch (err) {
      console.error("Erreur lors du changement de disponibilité:", err.message);
      alert("Impossible de changer la disponibilité");
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);

    const filePath = `animalia/${Date.now()}-${file.name}`;

    const { error } = await supabase.storage
      .from("animalia")
      .upload(filePath, file);

    if (error) {
      alert("Erreur lors de l'upload: " + error.message);
      setUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage
      .from("animalia")
      .getPublicUrl(filePath);
    console.log("image", urlData.publicUrl);
    if (urlData?.publicUrl) {
      setVariationForm((prev) => ({ ...prev, image: urlData.publicUrl }));
    }

    setUploading(false);
  };

  useEffect(() => {
    async function fetchProduct() {
      try {
        const { data, error } = await supabase
          .from("products")
          .select("*")
          .eq("id", id)
          .single();

        if (error) throw error;

        if (data.image) {
          const filename = data.image.split("/").pop();
          const { data: urlData } = supabase.storage
            .from("animalia")
            .getPublicUrl(filename);
          data.image = urlData?.publicUrl || data.image;
        }

        setProduct(data);
        setPromoPercentage(data.promo_percentage || "");
        refreshVariations(data.id);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchProduct();
  }, [id]);

  const refreshVariations = async (productId) => {
    const { data, error } = await supabase
      .from("product_variations")
      .select("*")
      .eq("product_id", productId);

    if (!error) setVariations(data);
  };

  // const handleAddOrUpdateVariation = async () => {
  //   const {
  //     name, price, weight, old_price, composition,
  //     analysis, ingredients, antioxidants, energy, image
  //   } = variationForm;

  //   if (!name || !price || !weight) return alert("Champs obligatoires manquants.");

  //   const payload = {
  //     product_id: product.id,
  //     variation_name: name,
  //     price,
  //     weight,
  //     old_price: old_price || null,
  //     composition: composition || null,
  //     analysis: analysis || null,
  //     ingredients: ingredients || null,
  //     antioxidants: antioxidants || null,
  //     energy: energy || null,
  //     image: image || null, // ✅ must include the uploaded URL
  //   };

  //   try {
  //     const { error } = editingVariation
  //       ? await supabase.from("product_variations").update(payload).eq("id", editingVariation.id)
  //       : await supabase.from("product_variations").insert(payload);

  //     if (error) throw error;

  //     refreshVariations(product.id);

  //     // ✅ Reset form including image
  //     setVariationForm({
  //       name: "", price: "", weight: "", old_price: "",
  //       composition: "", analysis: "", ingredients: "",
  //       antioxidants: "", energy: "", image: ""
  //     });

  //     setEditingVariation(null);
  //     setShowFormModal(false);
  //   } catch (err) {
  //     console.error(err);
  //     alert("Erreur lors de la sauvegarde: " + err.message);
  //   }
  // };


  const handleEditClick = (variation) => {
    setEditingVariation(variation);
    setVariationForm({
      name: variation.variation_name,
      price: variation.price,
      weight: variation.weight,
      old_price: variation.old_price || "",
      composition: variation.composition || "",
      analysis: variation.analysis || "",
      ingredients: variation.ingredients || "",
      antioxidants: variation.antioxidants || "",
      energy: variation.energy || "",
      image: variation.image || ""  // ✅ include existing image

    });

    setShowFormModal(true);
  };

  // ------------------ ADD / UPDATE VARIATION ------------------
  //   const handleAddOrUpdateVariation = async () => {
  //     const {
  //       name,
  //       price,
  //       weight,
  //       old_price,
  //       composition,
  //       analysis,
  //       ingredients,
  //       antioxidants,
  //       energy,
  //       image,
  //     } = variationForm;

  //     if (!name || !price || !weight) return alert("Champs obligatoires manquants.");

  //     const payload = {
  //       product_id: product.id,
  //       variation_name: name,
  //       price,
  //       weight,
  //       old_price: old_price || null,
  //       composition: composition || null,
  //       analysis: analysis || null,
  //       ingredients: ingredients || null,
  //       antioxidants: antioxidants || null,
  //       energy: energy || null,
  //       image: image || null,
  //     };

  //     try {
  //       const { data, error } = editingVariation
  //         ? await supabase
  //             .from("product_variations")
  //             .update(payload)
  //             .eq("id", editingVariation.id)
  //             .select()
  //         : await supabase.from("product_variations").insert(payload).select();

  //       if (error) throw error;

  //       console.log("Variation saved:", data);
  //       refreshVariations(product.id);
  //       setVariationForm({
  //         name: "",
  //         price: "",
  //         weight: "",
  //         old_price: "",
  //         composition: "",
  //         analysis: "",
  //         ingredients: "",
  //         antioxidants: "",
  //         energy: "",
  //         image: "",
  //       });
  //       setEditingVariation(null);
  //       setShowFormModal(false);
  //     } catch (err) {
  //       console.error("Error saving variation:", err);
  //       alert("Erreur lors de la sauvegarde: " + err.message);
  //     }
  //   };

  //   const handleEditClick = (variation) => {
  //     setEditingVariation(variation);
  //     setVariationForm({
  //       name: variation.variation_name,
  //       price: variation.price,
  //       weight: variation.weight,
  //       old_price: variation.old_price || "",
  //       composition: variation.composition || "",
  //       analysis: variation.analysis || "",
  //       ingredients: variation.ingredients || "",
  //       antioxidants: variation.antioxidants || "",
  //       energy: variation.energy || "",
  //       image: variation.image || "",
  //     });
  //     setShowFormModal(true);
  //   };

  const handleDeleteVariation = async (variation) => {
    if (!window.confirm("Supprimer cette variation ?")) return;
    const { error } = await supabase.from("product_variations").delete().eq("id", variation.id);
    if (!error) refreshVariations(product.id);
  };

  const handleTogglePromo = async () => {
    if (!product) return;

    setUpdatingPromo(true);

    try {
      const updatedPromo = !product.promo;

      if (updatedPromo) {
        // ========== ACTIVATING PROMO ==========
        const currentPrice = parseFloat(product.price_range);
        const percentage = promoPercentage || 10;
        const newPrice = currentPrice * (1 - percentage / 100);

        const updateData = {
          promo: true,
          promo_type: 'percentage', // Default to percentage when activating
          promo_percentage: percentage,
          price_range: newPrice,
          old_price: currentPrice // Store the original price
        };

        const { data, error } = await supabase
          .from("products")
          .update(updateData)
          .eq("id", product.id)
          .select()
          .single();

        if (error) throw error;

        setProduct(data);
        setPromoPercentage(data.promo_percentage || "");
        setProductPromoType('percentage');
        alert(`Promo activée avec ${percentage}% de réduction`);

      } else {
        // ========== DEACTIVATING PROMO ==========
        // Restore the original price from old_price
        const originalPrice = product.old_price
          ? parseFloat(product.old_price)
          : parseFloat(product.price_range);

        const updateData = {
          promo: false,
          promo_type: null,
          promo_percentage: null,
          price_range: originalPrice, // Restore original price
          old_price: null // Clear old_price
        };

        const { data, error } = await supabase
          .from("products")
          .update(updateData)
          .eq("id", product.id)
          .select()
          .single();

        if (error) throw error;

        setProduct(data);
        setPromoPercentage("");
        alert("Promo retirée avec succès. Prix d'origine restauré.");
      }

    } catch (error) {
      console.error("Error toggling promo:", error);
      alert("Erreur lors de la mise à jour de la promo: " + error.message);
    } finally {
      setUpdatingPromo(false);
    }
  };


  const handlePercentageChange = async () => {
    const percentage = parseInt(promoPercentage);

    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      alert("Veuillez entrer un pourcentage valide entre 0 et 100.");
      return;
    }

    try {
      // Get the base price (use old_price if exists, otherwise current price_range)
      const basePrice = product.old_price
        ? parseFloat(product.old_price)
        : parseFloat(product.price_range);

      // Calculate new discounted price
      const newPrice = basePrice * (1 - percentage / 100);

      const updateData = {
        promo_percentage: percentage,
        price_range: newPrice,
        promo_type: 'percentage'
      };

      // Store old_price only if it doesn't exist yet
      if (!product.old_price) {
        updateData.old_price = product.price_range;
      }

      const { data, error } = await supabase
        .from("products")
        .update(updateData)
        .eq("id", product.id)
        .select()
        .single();

      if (error) throw error;

      setProduct(data);
      alert(`Pourcentage mis à jour. Nouveau prix: ${newPrice.toFixed(3)} DT`);

    } catch (error) {
      console.error("Error updating percentage:", error);
      alert("Erreur lors de la mise à jour: " + error.message);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) return;
    setDeleting(true);
    const { error } = await supabase.from("products").delete().eq("id", id);
    if (!error) navigate("/admin/products");
    setDeleting(false);
  };


  if (loading) return <div>Chargement...</div>;
  if (error) return <div>Erreur: {error}</div>;
  if (!product) return <div>Produit non trouvé</div>;

  return (

    <>
      <div
        style={{
          minHeight: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "1rem",
          backgroundColor: "#f9f9f9",
          position: "relative" // <-- needed for absolute positioning if desired
        }}
      >

        <div className="container my-5" >

          <div className="card shadow p-4 d-flex flex-md-row flex-column gap-4 align-items-start" style={{ borderRadius: "16px", marginLeft: "-160px", height: "2700px", width: "1200px" }}>
            {product.image && (
              <img
                src={product.image}
                alt={product.name}
                className="img-fluid mb-3 mb-md-0"
                style={{ borderRadius: "12px", maxHeight: "300px", maxWidth: "300px", objectFit: "contain" }}
              />
            )}

            <div className="flex-grow-1 " style={{ marginLeft: "70px" }}>
              <h2 className="mb-3">{product.name}</h2>

              {product.promo && (
                <div
                  className="mb-4"
                  style={{
                    background: 'linear-gradient(135deg, #fff5f8 0%, #ffe6f0 100%)',
                    padding: '20px',
                    borderRadius: '16px',
                    border: '2px solid #ffcce0',
                    boxShadow: '0 4px 12px rgba(255, 105, 180, 0.15)'
                  }}
                >
                  {/* Header with badges */}
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span
                      className="badge"
                      style={{
                        fontSize: '0.9rem',
                        padding: '6px 14px',
                        background: 'linear-gradient(135deg, #ff69b4 0%, #ff1493 100%)',
                        color: '#fff',
                        borderRadius: '20px',
                        boxShadow: '0 2px 8px rgba(255, 105, 180, 0.3)'
                      }}
                    >
                      🎉 Promotion Active
                    </span>

                    {product.promo_type === 'percentage' && product.promo_percentage && (
                      <span
                        className="badge"
                        style={{
                          fontSize: '0.85rem',
                          padding: '5px 12px',
                          backgroundColor: '#28a745',
                          color: '#fff',
                          borderRadius: '18px'
                        }}
                      >
                        -{product.promo_percentage}% de réduction
                      </span>
                    )}

                    {product.promo_type === 'exact' && (
                      <span
                        className="badge"
                        style={{
                          fontSize: '0.85rem',
                          padding: '5px 12px',
                          backgroundColor: '#17a2b8',
                          color: '#fff',
                          borderRadius: '18px'
                        }}
                      >
                        Prix Spécial
                      </span>
                    )}
                  </div>

                  {/* Price Display */}
                  <div
                    className="mb-3 p-3"
                    style={{
                      backgroundColor: '#fff',
                      borderRadius: '12px',
                      border: '1px solid #ffe0f0'
                    }}
                  >
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      {/* Original Price */}
                      {product.old_price && (
                        <div>
                          <small style={{ color: '#999', fontSize: '0.8rem', display: 'block' }}>
                            Prix d'origine
                          </small>
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <span style={{
                              fontSize: '1.1rem',
                              color: '#aaa',
                              fontWeight: '600'
                            }}>
                              {parseFloat(product.old_price).toLocaleString('fr-FR', {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3
                              })} DT
                            </span>
                            {/* Strikethrough line */}
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '-2px',
                              right: '-2px',
                              height: '2px',
                              background: 'linear-gradient(90deg, transparent, #ff4444, transparent)',
                              transform: 'translateY(-50%) rotate(-5deg)'
                            }} />
                          </div>
                        </div>
                      )}

                      {/* Arrow */}
                      {product.old_price && (
                        <span style={{ fontSize: '1.5rem', color: '#28a745' }}>→</span>
                      )}

                      {/* Current Promo Price */}
                      <div>
                        <small style={{ color: '#28a745', fontSize: '0.8rem', display: 'block', fontWeight: '600' }}>
                          Prix promotionnel
                        </small>
                        <span style={{
                          fontSize: '1.8rem',
                          background: 'linear-gradient(135deg, #ff1493 0%, #ff69b4 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontWeight: '900',
                          letterSpacing: '-0.5px'
                        }}>
                          {parseFloat(product.price_range).toLocaleString('fr-FR', {
                            minimumFractionDigits: 3,
                            maximumFractionDigits: 3
                          })}
                          <span style={{ fontSize: '1rem', fontWeight: '700' }}> DT</span>
                        </span>
                      </div>

                      {/* Savings Display */}
                      {product.old_price && product.promo_type === 'percentage' && product.promo_percentage && (
                        <div
                          style={{
                            marginLeft: 'auto',
                            backgroundColor: '#d4edda',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            border: '1px solid #c3e6cb'
                          }}
                        >
                          <small style={{ color: '#155724', fontSize: '0.75rem', display: 'block', fontWeight: '600' }}>
                            Vous économisez
                          </small>
                          <span style={{ color: '#28a745', fontWeight: '800', fontSize: '1.1rem' }}>
                            💰 {(parseFloat(product.old_price) - parseFloat(product.price_range)).toLocaleString('fr-FR', {
                              minimumFractionDigits: 3,
                              maximumFractionDigits: 3
                            })} DT
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Promo Type Selector */}
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: '600', color: '#333', fontSize: '0.9rem' }}>
                      Type de promotion
                    </label>
                    <div className="btn-group d-flex" role="group">
                      <button
                        type="button"
                        className="btn"
                        style={{
                          backgroundColor: productPromoType === 'percentage' ? '#007bff' : '#e9ecef',
                          color: productPromoType === 'percentage' ? '#fff' : '#6c757d',
                          border: '2px solid ' + (productPromoType === 'percentage' ? '#007bff' : '#dee2e6'),
                          fontWeight: '600',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => handleProductPromoTypeChange('percentage')}
                      >
                        📊 Pourcentage {productPromoType === 'percentage' && '✓'}
                      </button>
                      <button
                        type="button"
                        className="btn"
                        style={{
                          backgroundColor: productPromoType === 'exact' ? '#28a745' : '#e9ecef',
                          color: productPromoType === 'exact' ? '#fff' : '#6c757d',
                          border: '2px solid ' + (productPromoType === 'exact' ? '#28a745' : '#dee2e6'),
                          fontWeight: '600',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => handleProductPromoTypeChange('exact')}
                      >
                        💰 Prix Exact {productPromoType === 'exact' && '✓'}
                      </button>
                    </div>
                  </div>

                  {/* Promo Input - Percentage */}
                  {productPromoType === 'percentage' && (
                    <div
                      className="p-3"
                      style={{
                        backgroundColor: '#f0f8ff',
                        borderRadius: '10px',
                        border: '1px solid #b3d9ff'
                      }}
                    >
                      <label className="form-label" style={{ fontWeight: '600', color: '#0056b3', fontSize: '0.9rem' }}>
                        Pourcentage de réduction
                      </label>
                      <div className="d-flex gap-2 align-items-center">
                        <input
                          type="number"
                          className="form-control"
                          style={{
                            maxWidth: '140px',
                            border: '2px solid #007bff',
                            fontWeight: '600'
                          }}
                          value={exactPromoPrice}
                          onChange={(e) => setExactPromoPrice(e.target.value)}
                          min="0"
                          max="100"
                          placeholder="10"
                        />
                        <span style={{ fontWeight: '600', color: '#007bff' }}>%</span>
                        <button
                          className="btn"
                          style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            fontWeight: '600',
                            padding: '8px 20px',
                            borderRadius: '8px'
                          }}
                          onClick={handlePercentageChange}
                        >
                          ✓ Appliquer le pourcentage
                        </button>
                      </div>
                      <small className="text-muted d-block mt-2">
                        Le prix sera automatiquement calculé: Prix d'origine × (1 - {promoPercentage || 0}%)
                      </small>
                    </div>
                  )}

                  {/* Promo Input - Exact Price */}
                  {productPromoType === 'exact' && (
                    <div
                      className="p-3"
                      style={{
                        backgroundColor: '#f0fff4',
                        borderRadius: '10px',
                        border: '1px solid #9ae6b4'
                      }}
                    >
                      <label className="form-label" style={{ fontWeight: '600', color: '#22543d', fontSize: '0.9rem' }}>
                        Prix promotionnel exact
                      </label>
                      <div className="d-flex gap-2 align-items-center">
                        <input
                          type="number"
                          className="form-control"
                          style={{
                            maxWidth: '160px',
                            border: '2px solid #28a745',
                            fontWeight: '600'
                          }}
                          value={exactPromoPrice}
                          onChange={(e) => setExactPromoPrice(e.target.value)}
                          min="0"
                          step="0.001"
                          placeholder={product.price_range}
                        />
                        {/* <span style={{ fontWeight: '600', color: '#28a745' }}>DT</span> */}
                        <button
                          className="btn"
                          style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            fontWeight: '600',
                            padding: '8px 20px',
                            borderRadius: '8px'
                          }}
                          onClick={handleProductExactPriceChange}
                        >
                          ✓ Définir le prix exact
                        </button>
                      </div>
                      <small className="text-muted d-block mt-2">
                        Définissez directement le nouveau prix sans calcul de pourcentage
                        <p style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px' }}>
                          ⚠️ Si le prix comporte des centimes, par exemple 10,500 DT, veuillez le saisir comme 10,5
                        </p>
                      </small>
                    </div>
                  )}
                </div>
              )}
              <div style={{ lineHeight: "1.6", marginTop: "1rem" }}>
                {/* Categories */}
                {product.category && (
                  <div style={{ marginBottom: "0.5rem" }}>
                    {(() => {
                      let cats = [];
                      try {
                        cats = JSON.parse(product.category);
                      } catch {
                        cats = [product.category]; // fallback if not JSON
                      }

                      return cats.map((cat, idx) => (
                        <span
                          key={idx}
                          style={{
                            display: "inline-block",
                            backgroundColor: "#ff41b4",
                            color: "#fff",
                            padding: "4px 10px",
                            borderRadius: "12px",
                            fontSize: "0.85rem",
                            marginRight: "6px",
                            marginBottom: "4px"
                          }}
                        >
                          {cat}
                        </span>
                      ));
                    })()}
                  </div>
                )}

                {/* Subcategories */}
                {product.subcategory && (
                  <div style={{ marginBottom: "0.5rem" }}>
                    {(() => {
                      let subs = [];
                      try {
                        subs = JSON.parse(product.subcategory);
                      } catch {
                        subs = [product.subcategory];
                      }

                      return subs.map((sub, idx) => (
                        <span
                          key={idx}
                          style={{
                            display: "inline-block",
                            backgroundColor: "#ffc107",
                            color: "#333",
                            padding: "4px 10px",
                            borderRadius: "12px",
                            fontSize: "0.85rem",
                            marginRight: "6px",
                            marginBottom: "4px"
                          }}
                        >
                          {sub}
                        </span>
                      ));
                    })()}
                  </div>
                )}

                {(product.weight || product.brand || product.price_range) && (
                  <div
                    style={{
                      background: "#f9fafb",
                      padding: "12px 16px",
                      borderRadius: "10px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      border: "1px solid #eee",
                    }}
                  >
                    {product.weight && (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "0.85rem", color: "#888" }}>⚖️</span>
                        <span style={{ fontSize: "0.9rem", color: "#333" }}>
                          <strong style={{ fontWeight: 600 }}>Poids:</strong> {product.weight}
                        </span>
                      </div>
                    )}

                    {product.brand && (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "0.85rem", color: "#888" }}>🏷️</span>
                        <span style={{ fontSize: "0.9rem", color: "#333" }}>
                          <strong style={{ fontWeight: 600 }}>Marque:</strong> {product.brand}
                        </span>
                      </div>
                    )}

                    {product.price_range && (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "0.85rem", color: "#888" }}>💰</span>
                        <span style={{ fontSize: "0.9rem", color: "#ff4cbb" }}>
                          <strong style={{ fontWeight: 700 }}>Prix:</strong> {product.price_range} DT
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {product.description && (
                  <p style={{ marginTop: "0.75rem", color: "#444", fontStyle: "italic", backgroundColor: "#fff0f6", padding: "0.5rem 0.75rem", borderRadius: "6px" }}>
                    <strong>Description:</strong> {product.description}
                  </p>
                )}
              </div>


              <button className="btn mb-3" style={{ backgroundColor: "#ff41b4", color: "white", marginTop: "10px" }} onClick={() => setShowVariationsModal(!showVariationsModal)}>
                {showVariationsModal ? "Masquer les variations" : "Gérer les variations"}
              </button>
              {showVariationsModal && (
                <>
                  <ul className="list-group mb-4">
                    {variations.map((variation) => (
                      <li key={variation.id} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-start flex-wrap">
                          <div style={{ flex: 1 }}>
                            {variation.image && (
                              <img
                                src={variation.image}
                                alt={variation.variation_name}
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                  marginRight: "10px"
                                }}
                              />
                            )}

                            <strong>{variation.variation_name}</strong> – {variation.weight}<br />
                            <div>
                              Prix:
                              <span style={{
                                textDecoration: variation.promo && variation.old_price ? 'line-through' : 'none',
                                color: variation.promo && variation.old_price ? '#999' : 'inherit',
                                marginRight: variation.promo && variation.old_price ? '8px' : '0'
                              }}>
                                {variation.old_price && variation.promo
                                  ? `${formatPrice(variation.old_price)} DT`
                                  : ''
                                }
                              </span>
                              <span style={{
                                color: variation.promo ? '#ff41b4' : 'inherit',
                                fontWeight: variation.promo ? 'bold' : 'normal'
                              }}>
                                {formatPrice(variation.price)} DT
                              </span>
                              {variation.promo && variation.promo_type === 'percentage' && (
                                <span style={{
                                  backgroundColor: '#ff41b4',
                                  color: 'white',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  marginLeft: '8px'
                                }}>
                                  -{variation.promo_percentage || 0}%
                                </span>
                              )}
                              {variation.promo && variation.promo_type === 'exact' && (
                                <span style={{
                                  backgroundColor: '#28a745',
                                  color: 'white',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  marginLeft: '8px'
                                }}>
                                  Prix Promo
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="btn-group mt-2 mt-md-0">
                            {/* Promo Toggle */}
                            <button
                              className="btn btn-sm"
                              style={{ backgroundColor: variation.promo ? "green" : "red", color: "white" }}
                              onClick={() => handleToggleVariationPromo(variation)}
                              disabled={updatingVariationPromo}
                            >
                              {variation.promo ? "Retirer Promo" : "Activer Promo"}
                            </button>

                            {/* Promo Type Buttons - Only show when promo is active */}
                            {variation.promo && (
                              <>
                                <button
                                  className="btn btn-sm"
                                  style={{
                                    backgroundColor: variation.promo_type === 'percentage' ? "#007bff" : "#6c757d",
                                    color: "white"
                                  }}
                                  onClick={() => openPromoPopup(variation, 'percentage')}
                                >
                                  % {variation.promo_type === 'percentage' ? '✓' : ''}
                                </button>


                                <button
                                  className="btn btn-sm"
                                  style={{
                                    backgroundColor: variation.promo_type === 'exact' ? "#28a745" : "#6c757d",
                                    color: "white"
                                  }}
                                  onClick={() => openPromoPopup(variation, 'exact')}
                                >
                                  💰 {variation.promo_type === 'exact' ? '✓' : ''}
                                </button>
                              </>
                            )}
                          </div>

                          <div className="btn-group mt-2 mt-md-0">
                            <button
                              className="btn btn-sm"
                              style={{ backgroundColor: variation.available ? "green" : "red", color: "white" }}
                              onClick={() => handleToggleAvailability(variation)}
                            >
                              {variation.available ? "Disponible" : "Indisponible"}
                            </button>
                            <button
                              className="btn btn-sm"
                              style={{ backgroundColor: "gray", color: "white" }}
                              onClick={() => handleEditClick(variation)}
                            >
                              ✏️
                            </button>
                            <button
                              className="btn btn-sm"
                              style={{ backgroundColor: "#ff41b4", color: "white" }}
                              onClick={() => handleDeleteVariation(variation)}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <button
                    className="btn"
                    style={{ backgroundColor: "gray", color: "white" }}
                    onClick={() => {
                      setEditingVariation(null);
                      setVariationForm({
                        name: "", price: "", weight: "", old_price: "",
                        composition: "", analysis: "", ingredients: "",
                        antioxidants: "", energy: "", image: ""
                      });
                      setShowFormModal(true);
                    }}
                  >
                    + Ajouter une variation
                  </button>
                </>
              )}

              <p className="text-muted mt-3"><small>Créé le: {new Date(product.created_at).toLocaleDateString()}</small></p>

              <div className="d-flex justify-content-between mt-4">
                <Link
                  to={`/admin/products`}
                  className="btn d-flex align-items-center gap-2"
                  style={{
                    backgroundColor: "#ff41b4",
                    color: "white",
                    padding: "0.25rem 0.75rem",
                    borderRadius: "1rem",
                    fontSize: "0.875rem",
                    height: "36px",
                    lineHeight: "1.2",
                  }}
                >
                  Retour <img src={pawPrint} alt="Retour" style={{ width: "20px" }} />
                </Link>

                <div className="d-flex gap-2">
                  <button
                    className="btn d-flex align-items-center gap-2"
                    onClick={handleTogglePromo}
                    disabled={updatingPromo}
                    style={{
                      backgroundColor: "#ff41b4",
                      color: "white",
                      padding: "0.25rem 0.75rem",
                      borderRadius: "1rem",
                      fontSize: "0.875rem",
                      height: "36px",
                      lineHeight: "1.2",
                    }}
                  >
                    {updatingPromo ? "Mise à jour..." : product.promo ? "Retirer Promo" : "Activer Promo"}
                    <img src={pawPrint} alt="Promo" style={{ width: "20px" }} />
                  </button>

                  <button
                    className="btn d-flex align-items-center gap-2"
                    onClick={handleDelete}
                    disabled={deleting}
                    style={{
                      backgroundColor: "#ff41b4",
                      color: "white",
                      padding: "0.25rem 0.75rem",
                      borderRadius: "1rem",
                      fontSize: "0.875rem",
                      height: "36px",
                      lineHeight: "1.2",
                    }}
                  >
                    {deleting ? "Suppression..." : "Supprimer"}
                    <img src={pawPrint} alt="Supprimer" style={{ width: "20px" }} />
                  </button>
                </div>
              </div>




            </div>

            <Modal show={showFormModal} onHide={() => setShowFormModal(false)}>
              <Modal.Header>
                <Modal.Title>
                  {editingVariation ? "Modifier une variation" : "Ajouter une variation"}
                </Modal.Title>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ background: "none", border: "none", fontSize: "1.5rem", lineHeight: "1", color: "#000" }}
                  aria-label="Fermer"
                  onClick={() => setShowFormModal(false)}
                >
                  ×
                </button>
              </Modal.Header>

              <Modal.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Nom de variation</Form.Label>
                    <Form.Control value={variationForm.name} onChange={(e) => setVariationForm({ ...variationForm, name: e.target.value })} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Poids</Form.Label>
                    <Form.Control value={variationForm.weight} onChange={(e) => setVariationForm({ ...variationForm, weight: e.target.value })} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Prix</Form.Label>
                    <Form.Control value={variationForm.price} onChange={(e) => setVariationForm({ ...variationForm, price: e.target.value })} />
                    <p style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px' }}>
                      ⚠️ Si le prix comporte des centimes, par exemple 10,500 DT, veuillez le saisir comme 10.5
                    </p>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Ancien prix</Form.Label>
                    <Form.Control value={variationForm.old_price} onChange={(e) => setVariationForm({ ...variationForm, old_price: e.target.value })} />
                    <p style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px' }}>
                      ⚠️ Si le prix comporte des centimes, par exemple 10,500 DT, veuillez le saisir comme 10.5
                    </p>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Composition</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={variationForm.composition}
                      onChange={(e) =>
                        setVariationForm({ ...variationForm, composition: e.target.value })
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Analyse</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={variationForm.analysis}
                      onChange={(e) =>
                        setVariationForm({ ...variationForm, analysis: e.target.value })
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Ingrédients</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={variationForm.ingredients}
                      onChange={(e) =>
                        setVariationForm({ ...variationForm, ingredients: e.target.value })
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Antioxydants</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={variationForm.antioxidants}
                      onChange={(e) =>
                        setVariationForm({ ...variationForm, antioxidants: e.target.value })
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Énergie</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={variationForm.energy}
                      onChange={(e) =>
                        setVariationForm({ ...variationForm, energy: e.target.value })
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Image de variation</Form.Label>
                    <Form.Control type="file" accept="image/*" onChange={handleImageUpload} />
                    {variationForm.image && (
                      <img src={variationForm.image} alt="Preview" style={{ marginTop: 10, maxHeight: 100, borderRadius: 8 }} />
                    )}

                  </Form.Group>


                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowFormModal(false)}>Annuler</Button>
                <Button
                  variant="primary"
                  style={{ backgroundColor: "#ff41b4", borderColor: "#ff41b4" }}
                  onClick={handleAddOrUpdateVariation}
                  disabled={uploading}  // ✅ prevent save until upload done
                >
                  {editingVariation ? "Modifier" : "Ajouter"}
                </Button>

                {/* <Button variant="primary" style={{ backgroundColor: "#ff41b4", borderColor: "#ff41b4" }} onClick={handleAddOrUpdateVariation}>
                                {editingVariation ? "Modifier" : "Ajouter"}
                            </Button> */}
              </Modal.Footer>
            </Modal>
          </div>
        </div>
        {showPromoPopup && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: "20px",
                borderRadius: "12px",
                width: "300px",
                textAlign: "center",
              }}
            >
              <h5>
                {currentPromoType === 'percentage' ? "Pourcentage de la promo" : "Prix exact de la promo"}
              </h5>

              <input
                type="number"
                value={promoInputValue}
                onChange={(e) => setPromoInputValue(e.target.value)}
                style={{ width: "100%", padding: "8px", margin: "10px 0", borderRadius: "6px", border: "1px solid #ccc" }}
                min={currentPromoType === 'percentage' ? 0 : 0}
                max={currentPromoType === 'percentage' ? 100 : undefined}
              />

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                <button
                  onClick={() => setShowPromoPopup(false)}
                  style={{ padding: "6px 12px", borderRadius: "6px", background: "#ccc", color: "#000", border: "none" }}
                >
                  Annuler
                </button>

                <button
                  onClick={submitPromoPopup}
                  style={{ padding: "6px 12px", borderRadius: "6px", background: "#28a745", color: "#fff", border: "none" }}
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        )}

      </div></>
  );
};

export default ProductDetails;
